<script setup lang="ts">
import { useId } from 'vue';
import { ListOrdered, Shuffle, SlidersHorizontal } from 'lucide-vue-next';
import type { OrderingChoice } from '@/utils/customPlaylistSchedule';

/**
 * The icon for one ordering option. The editor's Ordering menu and the
 * schedule card's ordering line both draw it.
 *
 * The numbers mean "starts at position 1", so only the two Position 1 options
 * have them:
 * - Shuffled – Resume (Loop): shuffle.
 * - Ordered – Resume (Loop): a plain list with a play badge. It carries on
 *   from where it was.
 * - Ordered – Position 1 (Loop): a numbered list with a go-round badge. It
 *   starts from the top and goes round again.
 * - Ordered – Position 1 (No Loop): the plain numbered list. It starts from the
 *   top and stops at the end.
 * - A saved mix none of the four describe: sliders.
 *
 * Lucide has no list with a badge, so those two are drawn here: the whole of
 * lucide's list or list-ordered, with lucide's play or rotate-ccw scaled down
 * onto the lower right corner. A mask cuts the list away around the badge, so
 * the gap shows whatever is behind it, even a highlighted menu row.
 * The icon is decorative: the option's label is always written beside it.
 */
defineProps<{ choice: OrderingChoice }>();

// Each icon on the page needs its own mask id.
const maskId = `ordering-icon-mask-${useId()}`;
</script>

<template>
    <Shuffle v-if="choice === 'shuffled_resume'" aria-hidden="true" data-testid="ordering-icon" :data-choice="choice" />
    <ListOrdered v-else-if="choice === 'position_1_no_loop'" aria-hidden="true" data-testid="ordering-icon" :data-choice="choice" />
    <svg
        v-else-if="choice === 'ordered_resume' || choice === 'position_1'"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        data-testid="ordering-icon"
        :data-choice="choice"
    >
        <mask :id="maskId">
            <rect width="24" height="24" fill="white" stroke="none" />
            <circle cx="19" cy="19" r="5.8" fill="black" stroke="none" />
        </mask>
        <g v-if="choice === 'ordered_resume'" :mask="`url(#${maskId})`">
            <path d="M3 12h.01" />
            <path d="M3 18h.01" />
            <path d="M3 6h.01" />
            <path d="M8 12h13" />
            <path d="M8 18h13" />
            <path d="M8 6h13" />
        </g>
        <g v-else :mask="`url(#${maskId})`">
            <path d="M10 12h11" />
            <path d="M10 18h11" />
            <path d="M10 6h11" />
            <path d="M4 10h2" />
            <path d="M4 6h1v4" />
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
        </g>
        <g v-if="choice === 'ordered_resume'" transform="translate(14.8 14.2) scale(0.4)" stroke-width="4.8">
            <polygon points="6 3 20 12 6 21 6 3" />
        </g>
        <g v-else transform="translate(14.2 14.2) scale(0.4)" stroke-width="4.6">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
        </g>
    </svg>
    <SlidersHorizontal v-else aria-hidden="true" data-testid="ordering-icon" :data-choice="choice" />
</template>
